'use client';

import { useEffect, type PropsWithChildren, useRef, useState } from 'react';

import { Toaster } from '@backyard-ui/core';
import { BackyardProvider, screens } from '@backyard-ui/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import mediaQuery from 'css-mediaquery';

import { setAppRegion, onPageLoad } from '@/infra/tracking/tracker';
import {
  GlobalProvider,
  createGlobalStore,
  type GlobalState,
} from '@/presenters/store/global';
import '@/infra/monitoring/datadog-monitor';
import { userContext } from '@/utils/user-context';

import AlgoliaSearchProvider from './AlgoliaSearchProvider';

interface ProvidersProps
  extends PropsWithChildren,
    Omit<GlobalState, 'session'> {
  shouldSendLocationToApp: boolean;
  region: GlobalState['session']['region'];
}

export function Providers(props: Readonly<ProvidersProps>) {
  const { server, region, shouldSendLocationToApp, children } = props;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const store = useRef(
    createGlobalStore({ server, session: { region } })
  ).current;

  useEffect(() => {
    onPageLoad();
  }, []);

  useEffect(() => {
    const userContextCookies = userContext.cookies.getAll();

    store.setState({
      session: userContextCookies,
    });
  }, [store]);

  useEffect(() => {
    if (
      !server.isOnMobileApp ||
      !store.getState().session.regionName ||
      !shouldSendLocationToApp
    ) {
      return;
    }

    setAppRegion({
      regionName: store.getState().session.regionName ?? '',
      region: store.getState().session.region,
    });
  }, [store, shouldSendLocationToApp, server.isOnMobileApp]);

  const serverMatchMedia = (query: string) => ({
    matches: mediaQuery.match(query, {
      //@ts-ignore
      type: 'screen',
      width:
        server.device === 'mobile'
          ? mediaQuery.parse(`screen and (min-width: ${screens.sm})`)[0]
              .expressions[0].value
          : mediaQuery.parse(`screen and (min-width: ${screens.xl})`)[0]
              .expressions[0].value,
    }),
  });

  return (
    <GlobalProvider value={store}>
      <QueryClientProvider client={queryClient}>
        <AlgoliaSearchProvider>
          <BackyardProvider
            components={{
              UseMediaQuery: {
                defaultProps: {
                  serverMatchMedia,
                },
              },
            }}
          >
            <Toaster />
            {children}
          </BackyardProvider>
          <ReactQueryDevtools />
        </AlgoliaSearchProvider>
      </QueryClientProvider>
    </GlobalProvider>
  );
}
